import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

/**
 * This layout pattern is a modified version of Every Layout's Stack component (https://every-layout.dev/layouts/stack/).
 * This version isn't set up for the `splitAfter` prop, which would group certain child elements to the top or bottom, such as in a card layout.
 * Since margin describes the relationship between two proximate elements, margin is set by a shared parent element.
 * The sole purpose of this component is to handle vertical spacing between flow elements.
 * To improve consistency across pages and apps, Design has begun using margin values that are multiples of 8.
 * As of 7/31/20, the Figma spec files (https://www.figma.com/file/ypoEgXVpq5rM51vRq7b8QX/Figure-Application?node-id=1617%3A4107) reflect the updated values.
 * HELOC Figma files will be gradually updated.
 * When designs are updated, we may want to consider limiting the values that can be passed to the `space` prop.
 */

/**
 * Vertical margin reset is added directly to the Stack component.
 * If we pass the `as` prop to render a `<ul>`, for example, instead of a `<div>`, `frontlib-gui` might apply unexpected vertical margins.
 * The `--space` value is inherited by a Stack's children. Inherited styles have no specificity and are easy to override if necessary.
 */
const Element = styled.div`
  --space: ${(props) => props.space};
  margin-top: 0;
  margin-bottom: 0;

  /**
   * Vertical margin reset.
   * Added because some frontlib-gui styles add margins that could create inconsistent spacing.
  */
  & > * {
    margin-top: 0;
    margin-bottom: 0;
  }

  /**
   * The default approach uses the child combinator (>) to target only to the direct children, i.e., one layer deep.
   * If the recursive prop is passed, the child combinator is removed so we can apply margin to nested elements.
   * The owl selector (* + *) ignores the first child of the Stack and will automatically add top margin to its other direct children.
   * Note: vertical margin is applied to block elements but not inline elements.
  */

  ${(props) =>
    props.recursive
      ? css`
          * + * {
            margin-top: var(--space);
          }
        `
      : css`
          > * + * {
            margin-top: var(--space);
          }
        `}
`;

const Stack = ({ children, ...props }) => <Element {...props}>{children}</Element>;

Stack.propTypes = {
  children: PropTypes.node.isRequired,
  /**
   * A CSS margin value.
   * Design would like us to use multiples of 8, e.g., .8rem, 1.6rem, 2.4rem, 3.2rem, 4rem, etc.
   * For accessibility reasons, `space` uses `rem` as a unit in order to support members who need customize their font sizes.
   * `px` can still be used, however.
   * Defaults to 3.2rem because that is the most commonly used spacing value.
   */
  space: PropTypes.string,
  /**
   * Whether the spaces apply recursively (i.e., regardless of nesting level).
   * Nesting Stack components may be a better option if the recursive prop adds unwanted margin to nested elements.
   */
  recursive: PropTypes.bool,
};

Stack.defaultProps = {
  space: '3.2rem',
  recursive: false,
};

export default Stack;
